import { IconButton, Tooltip } from '@mui/material';
import React, { useMemo } from 'react';

import { MenuIconProps } from './types';

/**
 * This component should be used for consistent menu item icon rendering.
 * Wraps a child Icon in Tooltip and optionally IconButton.
 *
 * If it has an onClick, we set the fontSize prop to be `small`,
 * and wrap the Icon in IconButton.
 *
 * If it doesn't have an onClick, we set the fontSize prop to be `default`.
 *
 * for this reason it's child should be a material SVG. Images or other
 * content will probably work,  * but you might see errors, because
 * we're expecting the child to have a `fontSize` prop.
 *
 * example: `<MenuIcon><Delete/></MenuIcon>`
 */
const MenuIcon: React.FunctionComponent<MenuIconProps> = (props) => {
  const { tooltip, onClick, children, buttonProps, tooltipProps } = props;

  const clonedChild = useMemo(
    () => React.cloneElement(children, { fontSize: onClick ? 'small' : 'medium' }),
    [children, onClick]
  );

  const renderedButton = useMemo(
    () =>
      onClick ? (
        <IconButton size='small' onClick={onClick} {...buttonProps}>
          {clonedChild}
        </IconButton>
      ) : (
        <></>
      ),
    [buttonProps, clonedChild, onClick]
  );

  const renderMenuIcon = () => {
    return (
      <Tooltip title={tooltip || ''} {...tooltipProps}>
        {onClick ? renderedButton : clonedChild}
      </Tooltip>
    );
  };

  return renderMenuIcon();
};

export default MenuIcon;
